/**
 * Grant Lattice Live — CMC WebCodecs player Soft≠ Soft invent Soft frames.
 *
 * GLL-2/9/11/12/18 (research-hardened):
 *  monoseq cursor Soft≠ wall-ms drop
 *  codec string from SPS / lattice Soft≠ hard avc1 (supports hev1 Annex-B)
 *  wait first key AU (param+IDR) after configure Soft≠ delta-first silent fail
 *  WHEP: Portless Sentry Soft≠ invent bare MediaMTX ports Soft grant-in-query
 *  AU plane is primary WAN (GLL-12 side-index scale Soft multi-api)
 *  GLL-18: wait_ms long-park + single-flight pull Soft 180ms setInterval lag
 *
 * Server never re-encodes. Optional Sentry WHEP ticket Soft never open :8889.
 */
import { useEffect, useRef, useState } from "react";
import { html, Alert, KV } from "./ui.js";
import { pullLiveViewAus, mintWhepSentryTicket } from "./udock-api.js";

function b64ToU8(b64) {
  const bin = atob(String(b64 || ""));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function annexB(nal) {
  const sc = new Uint8Array([0, 0, 0, 1]);
  const o = new Uint8Array(sc.length + nal.length);
  o.set(sc, 0);
  o.set(nal, sc.length);
  return o;
}

function familyOf(codec) {
  const c = String(codec || "").toLowerCase();
  if (c.startsWith("hev") || c.startsWith("hvc") || c.includes("hevc") || c.includes("h265")) {
    return "hevc";
  }
  if (c.startsWith("avc") || c.includes("h264")) return "h264";
  return "unknown";
}

/** LIVE_AU_CODEC_ADMIT_v2 Soft≠ configure/canvas thrash on TRAIL-only demuxed. */
export function webcodecsAdmitFromGrant(grant) {
  const t = (grant && grant.product_tracks) || {};
  if (t.gll_host_webcodecs === true) return { ok: true, reason: "gll_host_webcodecs" };
  if (t.gll_host_demuxed === true && t.gll_host_webcodecs !== true) {
    return { ok: false, reason: "hold_await_webcodecs_admit" };
  }
  // Pack without tracks: Soft-wait media-era lattice Soft≠ force play blind
  if (grant && (grant.has_param || grant.idr_seen) && grant.has_param && grant.idr_seen) {
    return { ok: true, reason: "grant_param_idr" };
  }
  if (!t || Object.keys(t).length === 0) {
    return { ok: false, reason: "hold_await_webcodecs_admit" };
  }
  return { ok: false, reason: "hold_await_webcodecs_admit" };
}

/**
 * @param {{
 *  deviceId: string,
 *  sessionId: string,
 *  grant?: object,
 *  mediaPlayed?: boolean,
 *  toast?: function,
 *  surface?: "mini"|"desk",
 * }} props
 */
export function GrantLatticePlayer({
  deviceId,
  sessionId,
  grant,
  mediaPlayed,
  toast,
  surface = "desk",
}) {
  const surfaceKind = surface === "mini" ? "mini" : "desk";
  // mini: longer empty floor Soft monoseq pressure Soft invent second stream
  const pullWaitMs = surfaceKind === "mini" ? 280 : 140;
  const emptyFloorMs = surfaceKind === "mini" ? 48 : 8;
  const canvasRef = useRef(null);
  const decoderRef = useRef(null);
  const cursorSeqRef = useRef(0);
  const configuredRef = useRef(false);
  const needKeyRef = useRef(true);
  const codecRef = useRef((grant && grant.codec) || "avc1.42C01E");
  const familyRef = useRef(familyOf(codecRef.current));
  const [status, setStatus] = useState("init");
  const [err, setErr] = useState("");
  const [stats, setStats] = useState({ nals: 0, keys: 0, recover: 0 });
  const [uiCodec, setUiCodec] = useState(codecRef.current);
  // GLL-13 portless ticket lattice Soft≠ invent bare :8889 Soft ICE public
  const [ticket, setTicket] = useState(null); // {whep_url, exp, ticket_id, uses_left}
  const [ticketNote, setTicketNote] = useState("");
  const ticketTimerRef = useRef(null);

  const admit = webcodecsAdmitFromGrant(grant);
  const canPlay =
    !!mediaPlayed &&
    grant &&
    grant.live_view !== false &&
    (grant.ready !== false || mediaPlayed) &&
    admit.ok;

  // GLL-13 auto-mint Soft refresh Soft multi-api Redis ticket Soft WebCodecs still primary
  useEffect(() => {
    if (!canPlay || !deviceId || !sessionId) return undefined;
    const mode = (grant && grant.whep_mode) || "";
    if (mode !== "sentry" && !String(grant && grant.whep_ticket_endpoint || "").includes("whep-ticket")) {
      setTicketNote("portless Sentry off · WebCodecs only");
      return undefined;
    }
    if (!(grant && grant.whep_ready) && mode === "sentry") {
      // ready requires pipe Soft still allow mint after media for probe Soft service gates media_played
    }
    let dead = false;
    const schedule = (ms) => {
      if (ticketTimerRef.current) clearTimeout(ticketTimerRef.current);
      ticketTimerRef.current = setTimeout(runMint, Math.max(5000, ms || 20000));
    };
    const runMint = async () => {
      if (dead) return;
      setTicketNote("minting sentry ticket…");
      const res = await mintWhepSentryTicket(deviceId, sessionId);
      if (dead) return;
      if (!res || !res.ok || !(res.data && res.data.ok)) {
        setTicketNote(String((res && (res.error || (res.data && res.data.reason))) || "mint_denied"));
        schedule(15000);
        return;
      }
      const d = res.data;
      setTicket({
        whep_url: d.whep_url,
        exp: d.exp,
        ticket_id: d.ticket_id,
        uses_left: d.uses_left,
        multi_api: d.multi_api,
        ice_public: d.ice_public === true,
        media_plane: d.media_plane || "webcodecs_preferred",
      });
      setTicketNote(
        "ticket ok · multi-api=" +
          (d.multi_api ? "1" : "0") +
          " · ice_public=" +
          (d.ice_public ? "1" : "0") +
          " · uses_left=" +
          String(d.uses_left ?? "—"),
      );
      const waitMs = Math.max(8000, Number(d.refresh_after_s || 20) * 1000);
      schedule(waitMs);
    };
    runMint();
    return () => {
      dead = true;
      if (ticketTimerRef.current) clearTimeout(ticketTimerRef.current);
    };
  }, [
    canPlay,
    deviceId,
    sessionId,
    grant && grant.whep_mode,
    grant && grant.whep_ready,
    grant && grant.whep_ticket_endpoint,
    grant && grant.product_tracks && grant.product_tracks.gll_host_webcodecs,
  ]);

  useEffect(() => {
    if (!canPlay || !deviceId || !sessionId) {
      if (mediaPlayed && grant && !admit.ok) {
        setStatus(String(admit.reason || "hold_await_webcodecs_admit"));
      }
      return undefined;
    }
    if (typeof VideoDecoder === "undefined") {
      setErr("WebCodecs VideoDecoder missing (need HTTPS/secure context) · Live");
      setStatus("no_webcodecs");
      return undefined;
    }
    let dead = false;
    const canvas = canvasRef.current;
    const ctx = canvas && canvas.getContext("2d");

    const hardReset = (why) => {
      try {
        if (decoderRef.current) {
          decoderRef.current.close();
          decoderRef.current = null;
        }
      } catch (_e) { /* ignore */ }
      configuredRef.current = false;
      needKeyRef.current = true;
      setStats((s) => ({ ...s, recover: s.recover + 1 }));
      setStatus("recover_" + (why || "key"));
    };

    const ensureDecoder = (codec) => {
      const fam = familyOf(codec);
      // Family switch Soft-reconfigure Soft≠ feed HEVC into avc1 decoder Soft silent fail
      if (
        decoderRef.current &&
        configuredRef.current &&
        fam === familyRef.current
      ) {
        return decoderRef.current;
      }
      if (decoderRef.current) {
        try { decoderRef.current.close(); } catch (_e) { /* ignore */ }
        decoderRef.current = null;
        configuredRef.current = false;
      }
      const dec = new VideoDecoder({
        output: (frame) => {
          try {
            if (ctx && canvas) {
              if (canvas.width !== frame.displayWidth) canvas.width = frame.displayWidth || 320;
              if (canvas.height !== frame.displayHeight) canvas.height = frame.displayHeight || 240;
              ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
            }
          } finally {
            frame.close();
          }
        },
        error: (e) => {
          setErr(String((e && e.message) || e));
          hardReset("dec_err");
        },
      });
      try {
        // Annex-B Soft: omit description (W3C AVC + HEVC live-stream path).
        const cstr = codec || codecRef.current || "avc1.42C01E";
        dec.configure({
          codec: cstr,
          optimizeForLatency: true,
          hardwareAcceleration: fam === "hevc" ? "prefer-hardware" : "no-preference",
          ...(fam === "hevc" ? { hevc: { format: "annexb" } } : { avc: { format: "annexb" } }),
        });
        configuredRef.current = true;
        needKeyRef.current = true;
        familyRef.current = fam;
        setUiCodec(cstr);
      } catch (e) {
        setErr(
          "configure: " +
            String((e && e.message) || e) +
            (fam === "hevc"
              ? " · HEVC may need browser HW decode Soft≠ invent soft SW"
              : ""),
        );
        setStatus("configure_fail");
        try { dec.close(); } catch (_e2) { /* ignore */ }
        return null;
      }
      decoderRef.current = dec;
      return dec;
    };

    const tick = async () => {
      if (dead) return;
      // GLL-18 long-park: reduce empty thrash Soft ~180ms fixed lag floor
      const out = await pullLiveViewAus(deviceId, sessionId, {
        max_n: surfaceKind === "mini" ? 16 : 32,
        after_seq: cursorSeqRef.current,
        wait_ms: pullWaitMs,
        view_token: (grant && grant.view_token) || "",
      });
      if (!out.ok) {
        setErr(out.error || "view pull refused");
        return;
      }
      const data = out.data || {};
      if (data.ready === false) {
        setStatus("awaiting_media");
        return;
      }
      if (data.codec) {
        codecRef.current = String(data.codec);
        setUiCodec(codecRef.current);
      }
      const items = data.items || [];
      if (!items.length) {
        setStatus(needKeyRef.current ? "waiting_idr" : "buffering");
        return;
      }
      const dec = ensureDecoder(codecRef.current);
      if (!dec) return;

      let nals = 0;
      let keys = 0;
      let tsBase = Math.floor(performance.now() * 1000);
      // Live HP: if decoder lagging Soft drop deltas Soft keep keys Soft wall-clock catch-up
      const laggy = typeof dec.decodeQueueSize === "number" && dec.decodeQueueSize > 8;
      for (const it of items) {
        const seq = Number(it.seq || 0);
        if (seq) cursorSeqRef.current = Math.max(cursorSeqRef.current, seq);
        if (it.codec) {
          codecRef.current = String(it.codec);
        }

        const raw = b64ToU8(it.nal_b64);
        const isKey = !!it.key;
        const dataAb = it.annexb ? raw : annexB(raw);

        // Soft-honest: never feed delta until first key after configure/flush.
        if (needKeyRef.current && !isKey) {
          continue;
        }
        if (needKeyRef.current && isKey) {
          needKeyRef.current = false;
        }
        if (laggy && !isKey) {
          continue;
        }
        if (isKey) keys += 1;
        nals += 1;
        try {
          dec.decode(
            new EncodedVideoChunk({
              type: isKey ? "key" : "delta",
              timestamp: tsBase,
              data: dataAb,
            }),
          );
          tsBase += 33000;
        } catch (_e) {
          hardReset("decode");
          break;
        }
      }
      if (nals > 0) {
        setStats((s) => ({ nals: s.nals + nals, keys: s.keys + keys, recover: s.recover }));
        setStatus("playing");
        setErr("");
      }
    };

    setStatus("connecting");
    // GLL-18 single-flight continuum Soft setInterval stampede (hidden bug under load)
    let gapTimer = 0;
    const loop = async () => {
      if (dead) return;
      const t0 = performance.now();
      try {
        await tick();
      } catch (e) {
        if (!dead) setErr(String((e && e.message) || e));
      }
      if (dead) return;
      const served = performance.now() - t0;
      // Server already parked when wait filled; only floor when instant empty reply.
      const floor = served >= 70 ? 0 : emptyFloorMs;
      gapTimer = window.setTimeout(loop, floor);
    };
    loop();
    return () => {
      dead = true;
      if (gapTimer) clearTimeout(gapTimer);
      hardReset("unmount");
    };
  }, [canPlay, deviceId, sessionId, grant && grant.view_token, surfaceKind, pullWaitMs, emptyFloorMs]);

  // surface embedded for Soft≠Live contracts (mini poll Soft desk)
  void surfaceKind;

  if (!mediaPlayed) {
    return html`<${Alert} tone="warn" title="Grant Lattice · awaiting media_played · Live"
      say="Waiting for the device to push remux media. The canvas stays empty until frames arrive."/>`;
  }
  if (!admit.ok) {
    return html`<${Alert} tone="warn" title="Grant Lattice · hold_await_webcodecs_admit · Live"
      say=${"LIVE_AU_CODEC_ADMIT · gll_host_webcodecs Soft≠ TRAIL-only · " + (admit.reason || "")}/>`;
  }
  if (grant && grant.live_view === false) {
    return html`<${Alert} tone="warn" title="UDOCK_LIVE_VIEW_ENABLED off"
      say=${grant.reason || "Live view flag disabled Soft≠ invent Soft player"}/>`;
  }

  const whepMode = (grant && grant.whep_mode) || "webcodecs_only";
  const sentryEp = (grant && grant.whep_ticket_endpoint) || "";
  // Portless GLL-13: ticket lattice Soft never green bare MediaMTX; ICE honesty
  const whepSignalOk = !!(ticket && ticket.whep_url && !ticket.ice_public);
  const whepOk = false; // Soft≠ invent browser WebRTC media Soft portless ICE closed
  const whepNote =
    (grant && grant.whep_reason) ||
    (whepMode === "sentry"
      ? "Sentry signaling ticket Soft WebCodecs pixel plane · ice_public=0"
      : grant && grant.whep_url && !grant.whep_ready
        ? "WHEP URL present Soft not ready (loopback / no pipe)"
        : "not configured · WebCodecs primary (GLL-12/13 AU scale)");

  return html`
    <section className="udock-live-view" data-cmc-live="grant-lattice" data-cmc-page="gll-18"
      data-surface=${surfaceKind}>
      <header className="udock-live-view__hd">
        <p className="udock-remux__eyebrow">GLL-18 · wait lattice · monoseq AU · high concurrency</p>
        <h4>Real video · Live</h4>
        <p className="udock-remux__lede">
          Soft-hardened: wait IDR Soft after configure · lattice codec avc1|hev1 · FU gap refuse.
          WAN = WebCodecs AU long-park Soft multi-api Redis · single-flight Soft setInterval lag.
        </p>
      </header>
      ${grant && grant.hold_i1
        ? html`<${Alert} tone="warn" title="I1 hold · Live"
            say="Alarm active Soft-honest: remux budget may starve live bandwidth."/>`
        : null}
      ${err ? html`<${Alert} tone="bad" title="Player · Live" say=${err}/>` : null}
      <div className="udock-live-view__stage">
        <canvas ref=${canvasRef} className="udock-live-view__canvas" width="320" height="240"
          aria-label="Live WebCodecs canvas Grant Lattice GLL-13"/>
      </div>
      <${KV} k="status" v=${status} tone=${status === "playing" ? "hi" : "warn"}/>
      <${KV} k="nals/keys/recover" v=${stats.nals + " / " + stats.keys + " / " + stats.recover}/>
      <${KV} k="codec" v=${uiCodec || "—"}/>
      <${KV} k="video_family" v=${(grant && grant.video_family) || familyOf(uiCodec)}/>
      <${KV} k="primary_track" v=${(grant && grant.primary_track) || "webcodecs_au"}/>
      <${KV} k="gll_host_webcodecs" v=${
        String(
          ((grant && grant.product_tracks) || {}).gll_host_webcodecs === true
            ? "true"
            : admit.ok
              ? "admit_soft · " + admit.reason
              : "false · " + (admit.reason || "hold"),
        )
      } tone=${admit.ok ? "hi" : "warn"}/>
      <${KV} k="whep_mode" v=${whepMode}/>
      <${KV} k="mtx_path" v=${(grant && grant.mtx_path) || "—"}/>
      <${KV} k="whep_ready" v=${whepOk ? "true" : "false · " + whepNote}
        tone=${whepOk ? "hi" : "warn"}/>
      ${sentryEp
        ? html`<${KV} k="whep_ticket_ep" v=${sentryEp}/>`
        : null}
      <${KV} k="sentry_ticket" v=${ticketNote || "—"} tone=${whepSignalOk ? "hi" : "warn"}/>
      ${ticket && ticket.ticket_id
        ? html`<${KV} k="ticket_id" v=${String(ticket.ticket_id).slice(0, 18) + "…"}/>`
        : null}
      ${ticket && ticket.whep_url
        ? html`<p className="note">signal URL (opaque, no grant/pass): <code>${ticket.whep_url}</code>
            · ICE public=${ticket.ice_public ? "yes" : "no · WebCodecs pixels"}</p>`
        : null}
      <p className="note">${(grant && grant.honesty) || ""}</p>
    </section>`;
}
